import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
// UI-1: design tokens (variables only, nothing visible) and the accessibility foundation
// (keyboard-only focus ring, reduced motion). See src/ui/.
import './ui/tokens.css';
import './ui/foundation.css';
import { initFocusModality } from './ui/focusModality';
import './i18n'; // initialise i18next before rendering
import App from './App';
import { ApiError } from './api/client';
import { messageFromError } from './utils/apiError';
import { showErrorToast } from './utils/toast';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { MsalProvider } from '@azure/msal-react';
import { msalInstance } from './utils/outlookUtils';
import ErrorBoundary from './components/shared/ErrorBoundary';

// Deploy breadcrumb — the running build's git SHA. Doubles as the live-verify
// signal for the SW force-reload: after an auto-reload this flips to the new SHA.
console.log('Aidey build:', process.env.REACT_APP_BUILD_SHA || 'dev');

// The focus ring shows for keyboard use only (src/ui/foundation.css).
initFocusModality();

// Safety-net: any write call that throws ApiError without a local .catch will
// land here. Shows a toast so the user sees the error instead of a silent stale UI.
//
// Block 233 (group 1): the message computation and the DOM both moved into
// utils/apiError.js and utils/toast.js so there is ONE implementation rather than
// this one plus whatever each component invented. Same guard, same message for every
// input this handler can receive, same element — see the no-op proof in the block.
window.addEventListener('unhandledrejection', (event) => {
  const err = event.reason;
  // Card at sign-up: the "Finish starting your free trial" screen is the message, not a toast.
  if (!(err instanceof ApiError) || err.isAuthError || err.isCardSetupRequired) return;
  showErrorToast(messageFromError(err));
});

if ('serviceWorker' in navigator) {
  // Single update mechanism: the SW force-reloads controlled tabs on activate
  // (see public/sw.js). This message is the fallback for browsers where SW-side
  // client.navigate() is unreliable (notably Safari). A guard prevents a double
  // reload if navigate already fired.
  navigator.serviceWorker.addEventListener('message', (e) => {
    if (e.data && e.data.type === 'SW_RELOAD' && !window.__aideySwReloaded) {
      window.__aideySwReloaded = true;
      window.location.reload();
    }
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(reg => {
        console.log('Aidey SW registered:', reg.scope);

        // Force update checks so a deploy is discovered — and thus force-reloaded
        // — on long-lived tabs / installed PWAs (esp. Safari, which rarely
        // re-checks sw.js on its own) within one focus, no manual clear.
        const check = () => reg.update().catch(() => {});
        document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') check(); });
        window.addEventListener('focus', check);
        setInterval(check, 30 * 60 * 1000); // every 30 minutes
      })
      .catch(err => console.log('SW registration failed:', err));
  });
}

const root = ReactDOM.createRoot(document.getElementById('root'));

// Google OAuth Provider setup
const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || null;

root.render(
  <ErrorBoundary>
    {/* React.StrictMode disabled to prevent double mounting in development */}
    {googleClientId ? (
      <GoogleOAuthProvider clientId={googleClientId}>
        <MsalProvider instance={msalInstance}>
          <App />
        </MsalProvider>
      </GoogleOAuthProvider>
    ) : (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Google Calendar Integration Not Configured</h2>
        <p>Please add REACT_APP_GOOGLE_CLIENT_ID to your .env file</p>
      </div>
    )}
  </ErrorBoundary>
);
